import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import GenericTable from 'shared/components/GenericTable/genericTable';
import { format } from 'date-fns';
import { CONFIG_APP } from '../../../../shared/const/app.config';
import CurrencyComponent from '../../../../shared/components/Currency/Currency';
import SoftBadge from 'components/SoftBadge';
import { UserDataContext } from 'shared/context/userDataContext';

const path = 'POLICY_CANCELLATIONS';

const statusBadgeColor = {
  PENDING_CANCELLATION: 'info',
  APPROVED: 'success',
  DISMISSED: 'secondary',
};

const PolicyCancellationsTable = ({ data, tableUuid }) => {
  const { t } = useTranslation();
  const {
    businessUnits,
    selectedBusinessUnit,
    saveBusinessUnit,
    savePrevBusinessUnit,
    fetchShopId,
    selectedBrand,
    userRoles,
  } = useContext(UserDataContext);

  const navigate = useNavigate();

  const ensurePolicyBrand = async (rowDetails) => {
    if (userRoles.viewAll && rowDetails.businessUnitCode) {
      let addToList = false;
      let businessUnit =
        businessUnits.find(
          (unit) => unit.shopId === rowDetails.businessUnitCode,
        ) || null;
      savePrevBusinessUnit(selectedBusinessUnit);

      if (!businessUnit) {
        const businessUnitResponse = await fetchShopId(
          rowDetails.businessUnitCode,
        );
        if (!businessUnitResponse[0]) {
          throw new Error(
            '[policy-cancellations-table] businessUnit id not found',
          );
        }
        businessUnit = {
          ...businessUnitResponse[0],
          brand:
            businessUnitResponse[0]?.default_brand?.name ?? selectedBrand.brand,
        };
        addToList = true;
      }
      saveBusinessUnit(businessUnit, addToList);
    }
  };

  const goToCancellationManagement = async (rowDetails) => {
    const { policyId } = rowDetails || {};
    if (!policyId) {
      throw new Error('[policy-cancellations-table] policyId is undefined');
    }
    await ensurePolicyBrand(rowDetails);
    navigate(`/policy-details/${policyId}?cancellation=1`);
  };

  const columns = [
    {
      label: `${t(`${path}.submittedDate`)}`,
      value: (row) =>
        row.submittedDate
          ? format(new Date(row.submittedDate), CONFIG_APP.DATE_FORMAT)
          : '-',
      width: '150px',
    },
    {
      label: `${t(`${path}.policyId`)}`,
      value: (row) => row.policyId,
    },
    {
      label: `${t(`${path}.customer`)}`,
      value: (row) => row.customerName || '-',
    },
    {
      label: `${t(`${path}.product`)}`,
      value: (row) => row.product || '-',
    },
    {
      label: `${t(`${path}.destination`)}`,
      value: (row) => row.destination || '-',
    },
    {
      label: `${t(`${path}.submittedBy`)}`,
      value: (row) => row.submittedByAgentId || '-',
    },
    {
      label: `${t(`${path}.requestStatus`)}`,
      value: (row) =>
        row.requestStatus ? (
          <SoftBadge
            variant="contained"
            color={statusBadgeColor[row.requestStatus] || 'secondary'}
            badgeContent={t(`${path}.STATUS.${row.requestStatus}`)}
            container
          />
        ) : (
          <></>
        ),
    },
    {
      label: `${t(`${path}.amount`)}`,
      value: (row) =>
        row.amount ? (
          <CurrencyComponent
            value={row.amount.amount}
            countryCode={row.amount.currency}
            showCountryCode
          ></CurrencyComponent>
        ) : null,
    },
  ];

  return (
    <div data-testid="PolicyCancellationsTable">
      {!data || data.length === 0 ? (
        <p className="dataError">{t(`${path}.noPolicyCancellations`)}</p>
      ) : (
        <GenericTable
          tableUuid={tableUuid}
          data={data}
          columns={columns}
          path={path}
          onRowClick={(row) => goToCancellationManagement(row)}
        />
      )}
    </div>
  );
};

PolicyCancellationsTable.propTypes = {
  data: PropTypes.array.isRequired,
  tableUuid: PropTypes.string,
};

export default PolicyCancellationsTable;
